import React, { useEffect, useState } from 'react'
import API from '../../../services/API'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import ListSubheader from '@mui/material/ListSubheader'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import QrCodeScanner from '@mui/icons-material/QrCodeScanner'
import QRCodeReader from '../../../helpers/QRCodeReader'
import { parseSealScan, sealsMatch } from '../../../helpers/sealScan'

const getBagLocationLabel = (bag) => {
  if (bag.assignedToEmployee?.userAccount) {
    const { firstName, lastName } = bag.assignedToEmployee.userAccount
    return `with ${[firstName, lastName].filter(Boolean).join(' ')}`
  }
  if (bag.assignedToVehicle?.callSign) return `on ${bag.assignedToVehicle.callSign}`
  if (bag.assignedToEvent?.title) return `at ${bag.assignedToEvent.title}`
  if (bag.assignedToStore?.name) return `in ${bag.assignedToStore.name}`
  return 'unassigned'
}

// Lists every bag (grouped: this vehicle's bags first) so a crew can both
// confirm the bags already aboard and pull a bag from stores/another
// location onto this vehicle as part of the check. Bags not currently on
// the vehicle are flagged `assignToVehicle` - the submitting form reassigns
// them via /medsmx/newlocation (which writes the medicine log entry).
const DrugBags = ({ drugBags, setDrugBags, vehicleId }) => {
  const [drugBagOptions, setDrugBagOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentlySelectedBag, setCurrentlySelectedBag] = useState('')
  const [tagged, setTagged] = useState(true)
  const [tagMatch, setTagMatch] = useState(false)
  // QR seal tags: scanning the seal's code answers "is the tag X?" with
  // proof the checker was at the bag. tagScanned travels with the entry
  // inside the VDI's free-form drugBags array.
  const [scanOpen, setScanOpen] = useState(false)
  const [tagScanned, setTagScanned] = useState(false)
  const [scanMismatch, setScanMismatch] = useState(null)

  const hasRecordedTag = (bag) =>
    !!bag?.tagNumber && bag.tagNumber !== 'Removed'

  useEffect(() => {
    setLoading(true)
    API.get('/medsmx/bags?populateassignments=true')
      .then((res) => res.status === 200 && setDrugBagOptions(res.data))
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }, [])

  const isOnThisVehicle = (bag) =>
    !!vehicleId &&
    (bag.assignedToVehicle?._id ?? bag.assignedToVehicle) === vehicleId

  const handleAddBag = () => {
    const bag = drugBagOptions.find((x) => x._id === currentlySelectedBag)
    if (!bag) return
    setDrugBags((prev) => [
      ...prev,
      {
        bag: currentlySelectedBag,
        tagged,
        tagMatch: tagged ? tagMatch : false,
        tagScanned: tagged ? tagScanned : false,
        assignToVehicle: !isOnThisVehicle(bag),
        // The record says there's a seal but the checker found none - the
        // submitting form reports it via /medsmx/report-seal-removed.
        reportSealRemoved: !tagged && hasRecordedTag(bag),
      },
    ])
    setCurrentlySelectedBag('')
    setTagged(true)
    setTagMatch(false)
    setTagScanned(false)
    setScanMismatch(null)
  }

  const handleSealScan = (decodedText) => {
    const code = parseSealScan(decodedText)
    if (!code) return
    const bag = drugBagOptions.find((x) => x._id === currentlySelectedBag)
    const matches = sealsMatch(code, bag?.tagNumber)
    setTagMatch(matches)
    setTagScanned(true)
    setScanMismatch(matches ? null : code)
  }

  const handleRemoveBag = (bagId) => {
    setDrugBags((prev) => prev.filter((entry) => entry.bag !== bagId))
  }

  const availableOptions = drugBagOptions.filter(
    (x) => !drugBags.some((y) => y.bag === x._id),
  )
  const vehicleOptions = availableOptions.filter(isOnThisVehicle)
  const otherOptions = availableOptions.filter((bag) => !isOnThisVehicle(bag))

  return (
    <Stack component={Paper} padding={1} gap={1}>
      <Typography variant="title2">Drugs Bags</Typography>
      <Alert severity="warning" color="primary">
        Confirm each bag aboard one at a time. Selecting a bag that is
        currently elsewhere will assign it to this vehicle when the check is
        submitted. If this section is not applicable, you can leave it blank.
      </Alert>
      {!loading && vehicleId && vehicleOptions.length === 0 && drugBags.length === 0 && (
        <Alert severity="info">
          No medicine bag is currently assigned to this vehicle — you can
          select one below to bring it aboard.
        </Alert>
      )}
      {drugBags.map((entry) => {
        const bag = drugBagOptions.find((x) => x._id === entry.bag)
        return (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            padding={1}
            key={entry.bag}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
          >
            <Typography flex={1} fontWeight={700}>
              {bag?.name ?? 'Medicine bag'}
            </Typography>
            <Chip
              size="small"
              label={
                entry.tagged === false
                  ? 'Not tagged'
                  : entry.tagMatch
                    ? 'Tag matches'
                    : 'Tag not confirmed'
              }
              color={
                entry.tagged === false
                  ? 'warning'
                  : entry.tagMatch
                    ? 'success'
                    : 'default'
              }
              sx={{ borderRadius: 1 }}
            />
            {entry.tagScanned && entry.tagMatch && (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label="Verified by scan"
                sx={{ borderRadius: 1 }}
              />
            )}
            {entry.reportSealRemoved && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label="Seal will be reported removed"
                sx={{ borderRadius: 1 }}
              />
            )}
            {entry.assignToVehicle && (
              <Chip
                size="small"
                color="info"
                label="Will be assigned to this vehicle"
                sx={{ borderRadius: 1 }}
              />
            )}
            <IconButton
              size="small"
              onClick={() => handleRemoveBag(entry.bag)}
              aria-label="Remove bag"
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Box>
        )
      })}
      {availableOptions.length > 0 && (
        <>
          <FormControl fullWidth size="small">
            <InputLabel>Select a Bag</InputLabel>
            <Select
              label="Select a Bag"
              value={currentlySelectedBag}
              onChange={(e) => {
                setCurrentlySelectedBag(e.target.value)
                setTagged(true)
                setTagMatch(false)
              }}
            >
              {vehicleOptions.length > 0 && (
                <ListSubheader>On this vehicle</ListSubheader>
              )}
              {vehicleOptions.map((bag) => (
                <MenuItem value={bag._id} key={bag._id}>
                  {bag.name}
                </MenuItem>
              ))}
              {otherOptions.length > 0 && (
                <ListSubheader>Other bags</ListSubheader>
              )}
              {otherOptions.map((bag) => (
                <MenuItem value={bag._id} key={bag._id}>
                  {bag.name} — {getBagLocationLabel(bag)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {currentlySelectedBag && (
            <>
              <FormControlLabel
                label="Is the bag tagged?"
                control={
                  <Switch
                    onChange={(e) => {
                      setTagged(e.target.checked)
                      if (!e.target.checked) setTagMatch(false)
                    }}
                    checked={tagged}
                    color="success"
                  />
                }
              />
              {tagged && (
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  <FormControlLabel
                    label={`Is the tag ${
                      drugBagOptions.find((x) => x._id === currentlySelectedBag)
                        ?.tagNumber
                    }?`}
                    control={
                      <Switch
                        onChange={(e) => {
                          setTagMatch(e.target.checked)
                          setTagScanned(false)
                          setScanMismatch(null)
                        }}
                        checked={tagMatch}
                        color="success"
                      />
                    }
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<QrCodeScanner />}
                    onClick={() => setScanOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Scan seal
                  </Button>
                  {tagScanned && tagMatch && (
                    <Chip size="small" color="success" label="Verified by scan" />
                  )}
                  {tagScanned && scanMismatch && (
                    <Chip
                      size="small"
                      color="error"
                      label={`Scanned ${scanMismatch} — does not match`}
                    />
                  )}
                </Stack>
              )}
              {!tagged &&
                hasRecordedTag(
                  drugBagOptions.find((x) => x._id === currentlySelectedBag),
                ) && (
                  <Alert severity="warning">
                    This bag is recorded with seal{' '}
                    {
                      drugBagOptions.find((x) => x._id === currentlySelectedBag)
                        ?.tagNumber
                    }
                    . Submitting the check will report the seal as removed.
                  </Alert>
                )}
            </>
          )}
          <Button
            onClick={handleAddBag}
            type="button"
            size="small"
            variant="contained"
            color="success"
            disabled={!currentlySelectedBag}
          >
            Add Bag
          </Button>
        </>
      )}
      <QRCodeReader
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScan={handleSealScan}
      />
    </Stack>
  )
}

export default DrugBags
